import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { fetchSupportMessages } from '../../api/zabota_chat_api';
import { SUPPORT_CHAT_POLL_MS, SUPPORT_CHAT_URL_REFRESH_MS } from './constants';

function mergeMessages(prev, incoming) {
  const existingIds = new Set(prev.map((message) => message.id));
  const merged = [...prev, ...incoming.filter((message) => !existingIds.has(message.id))];
  merged.sort((a, b) => a.id - b.id);
  return merged;
}

let pendingIdCounter = 0;
const nextPendingId = () => `pending-${Date.now()}-${++pendingIdCounter}`;

/**
 * Polls `GET /chat/messages` for the given cabinet user.
 * Uses `after_id` for cheap incremental polling, and periodically does a
 * full refresh to pick up fresh signed attachment URLs (they expire fast).
 * Polling pauses while the tab is hidden and resumes on focus.
 *
 * Also tracks "pending" messages the user just sent, so they show up in the
 * chat instantly, before the server round-trip (send + Telegram forward +
 * next poll) confirms them.
 */
export function useSupportChatMessages(cabinetUserId) {
  const [confirmedMessages, setConfirmedMessages] = useState([]);
  const [pendingMessages, setPendingMessages] = useState([]);
  const [loading, setLoading] = useState(true);

  const lastMessageIdRef = useRef(null);
  const lastFullRefreshAtRef = useRef(0);
  const pollTimeoutRef = useRef(null);
  const pollIntervalRef = useRef(SUPPORT_CHAT_POLL_MS);

  const loadMessages = useCallback(
    async (fullRefresh = false) => {
      if (!cabinetUserId) return;

      try {
        const afterId = fullRefresh ? undefined : lastMessageIdRef.current ?? undefined;
        const rows = await fetchSupportMessages(cabinetUserId, afterId);

        if (fullRefresh) {
          setConfirmedMessages([...rows].sort((a, b) => a.id - b.id));
          lastFullRefreshAtRef.current = Date.now();
        } else if (rows.length) {
          setConfirmedMessages((prev) => mergeMessages(prev, rows));
        }

        if (rows.length) {
          lastMessageIdRef.current = Math.max(lastMessageIdRef.current ?? 0, ...rows.map((row) => row.id));
        }

        pollIntervalRef.current = SUPPORT_CHAT_POLL_MS;
      } catch (error) {
        console.error(error);
        pollIntervalRef.current = Math.min(pollIntervalRef.current * 2, 60000);
      } finally {
        setLoading(false);
      }
    },
    [cabinetUserId]
  );

  useEffect(() => {
    if (!cabinetUserId) {
      setLoading(false);
      return undefined;
    }

    let cancelled = false;

    const schedulePoll = () => {
      if (cancelled) return;
      if (pollTimeoutRef.current) clearTimeout(pollTimeoutRef.current);

      pollTimeoutRef.current = setTimeout(async () => {
        if (cancelled) return;

        // Пропускаем сетевой запрос, пока вкладка скрыта, но цикл продолжаем
        // планировать дальше — иначе после сворачивания вкладки polling
        // остановится навсегда (onVisibilityChange делает только разовый refetch).
        if (!document.hidden) {
          const isUrlRefreshDue = Date.now() - lastFullRefreshAtRef.current >= SUPPORT_CHAT_URL_REFRESH_MS;
          await loadMessages(isUrlRefreshDue);
        }

        schedulePoll();
      }, pollIntervalRef.current);
    };

    loadMessages(true).then(() => {
      if (!cancelled) schedulePoll();
    });

    const onVisibilityChange = () => {
      if (document.hidden) return;
      pollIntervalRef.current = SUPPORT_CHAT_POLL_MS;
      loadMessages(true);
    };
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', onVisibilityChange);
      if (pollTimeoutRef.current) clearTimeout(pollTimeoutRef.current);
    };
  }, [cabinetUserId, loadMessages]);

  const addPendingMessage = useCallback((message) => {
    const id = nextPendingId();
    setPendingMessages((prev) => [...prev, { ...message, id, pending: true }]);
    return id;
  }, []);

  const removePendingMessage = useCallback((id) => {
    setPendingMessages((prev) => prev.filter((message) => message.id !== id));
  }, []);

  const messages = useMemo(() => [...confirmedMessages, ...pendingMessages], [confirmedMessages, pendingMessages]);

  return { messages, loading, refetchMessages: loadMessages, addPendingMessage, removePendingMessage };
}
